import React from 'react'
import { Provider } from 'react-redux'
import { QueryClientProvider, QueryClient } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Router } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import TagManager from 'react-gtm-module'
import { Magento } from 'lib/core/services/magento'
import { RouteInterface, RoutePath, Context } from 'lib/types'
import { configureStore } from 'lib/core/data'
import { CoreContext } from 'lib/core/resources/context'
import { DEFAULT_PATH } from 'lib/core/resources/constants'
import { ApolloProvider } from 'lib/core/components/ApolloProvider'
import { IntlProvider } from 'lib/core/components/IntlProvider'

const magento = new Magento()
const store = configureStore(magento)
const history = createBrowserHistory()
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2.5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  }
})

if (process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID) {
  TagManager.initialize({
    gtmId: process.env.REACT_APP_GOOGLE_TAG_MANAGER_ID
  })
}

type Props = Omit<Context, 'path'> & { children: React.ReactElement | React.ReactElement[] }

export const PWA = (props: Props) => {
  const path = createPath(props.route)
  magento.extend(props.data?.configureMagentoQueries, props.data?.configureMagentoMutations)

  return (
    <CoreContext.Provider value={{ ...props, path, magentoClient: magento.getClient() }}>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <ApolloProvider>
            <IntlProvider>
              <Router history={history}>
                {props.children}
              </Router>
            </IntlProvider>
          </ApolloProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </Provider>
    </CoreContext.Provider>
  )
}

const createPath = (route?: RouteInterface): RoutePath => {
  return {
    login: route?.login?.path ?? DEFAULT_PATH.login,
    confirmAccount: route?.confirmAccount?.path ?? DEFAULT_PATH.confirmAccount,
    resetPassword: route?.resetPassword?.path ?? DEFAULT_PATH.resetPassword,
    createAccount: route?.createAccount?.path ?? DEFAULT_PATH.createAccount,
    forgotPassword: route?.forgotPassword?.path ?? DEFAULT_PATH.forgotPassword,
    cart: route?.cart?.path ?? DEFAULT_PATH.cart,
    checkout: route?.checkout?.path ?? DEFAULT_PATH.checkout,
    customer: route?.customer?.path ?? DEFAULT_PATH.customer,
    search: route?.search?.path ?? DEFAULT_PATH.search,
    paypalExpress: route?.paypalExpress?.path ?? DEFAULT_PATH.paypalExpress
    // wishlist: '/customer/account/wishlist' // TODO: add Wishlist
  }
}
